import React, { useState } from 'react'
import ReactDOM from 'react-dom'
import { FeatureFlagsEnum } from 'src/editor/shared/growthbook/feature-flags-enum'
import { useFeatureFlag } from 'src/editor/shared/growthbook/use-feature-flag'
import { twMerge } from 'tailwind-merge'
import { useDiagnostics } from '../hooks/use-diagnostics'
import { useDiagnosticsSummary } from '../hooks/use-diagnostics-summary'
import { Severity } from '../types'
import { DiagnosticsDrawer } from './diagnostics-drawer'

const isMockMode = process.env.APP_ENV === 'mock'
const isDevEnv =
  process.env.APP_ENV === 'mock' || process.env.NODE_ENV === 'development'

const badgeBgClasses: Record<Severity, string> = {
  high: 'bg-red-500',
  warning: 'bg-amber-500',
  info: 'bg-blue-500',
}

/**
 * Gating wrapper — prevents diagnostics engine from running
 * when the feature flag is off (avoids O(entity-count) work
 * on every state change for users without the flag).
 */
export const DiagnosticsLauncher = () => {
  const isEnabled = useFeatureFlag(FeatureFlagsEnum.Diagnostics)

  if (!isEnabled && !isMockMode) {
    return null
  }

  return <DiagnosticsLauncherInner />
}

const DiagnosticsLauncherInner = () => {
  const [isOpen, setIsOpen] = useState(false)
  const findings = useDiagnostics()
  const summary = useDiagnosticsSummary(findings)

  const badgeCount = summary.high || summary.total
  const badgeBgClass = summary.maxSeverity
    ? badgeBgClasses[summary.maxSeverity]
    : 'bg-red-500'

  return ReactDOM.createPortal(
    <>
      <button
        type="button"
        className={twMerge(
          'right-4 z-(--z-index-diagnostics-launcher) flex h-11 w-11 cursor-pointer items-center justify-center overflow-visible rounded-full border-none p-0 shadow-lg',
          isDevEnv ? 'bottom-[72px]' : 'bottom-4',
        )}
        style={{ position: 'fixed' }} // NOTE: fixed classname is overwritten by common/utils/GlobalStyles.js (62)
        onClick={() => setIsOpen(prev => !prev)}
        title="Page Diagnostics"
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="44"
          height="44"
          viewBox="0 0 256 256"
          role="img"
          aria-label="Page Diagnostics"
        >
          <circle cx="128" cy="128" r="128" fill="#b91c1c" />
          <g
            fill="none"
            stroke="#fff"
            strokeWidth="8"
            transform="translate(128,128)"
          >
            <ellipse rx="70" ry="28" />
            <ellipse rx="70" ry="28" transform="rotate(60)" />
            <ellipse rx="70" ry="28" transform="rotate(120)" />
          </g>
          <circle cx="128" cy="128" r="10" fill="#fff" />
        </svg>
        {badgeCount > 0 && (
          <span
            className={twMerge(
              'absolute -top-1 -right-1 flex h-5 w-5 items-center justify-center rounded-full font-bold text-[11px] text-white leading-none',
              badgeBgClass,
            )}
          >
            {badgeCount > 99 ? '99+' : badgeCount}
          </span>
        )}
      </button>
      {isOpen && (
        <DiagnosticsDrawer
          findings={findings}
          onClose={() => setIsOpen(false)}
        />
      )}
    </>,
    document.body,
  )
}
